import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Button,
  Checkbox,
  CustomDropdown,
  Input,
  ThemeProvider,
  time,
} from '@dr.pogodin/react-utils';

import Avatar from 'components/Avatar';
import Throbber from 'components/Throbber';

import {
  type ArtistT,
  type UserT,
  isValidPhone,
  isValidPhoneOrEmpty,
} from 'utils';

import dropdownTheme from 'themes/CustomDropdown/admin/with-inner-label-60px.scss';
import inputThemeFixedSmallLabel
  from 'themes/Input/admin/with-inner-fixed-small-label-60px.scss';
import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

import S from './style.scss';

type UpdateT = {
  newAvatar?: File;
  practitioner?: {
    teachingAndContentCreationConsent?: boolean;
    user?: Partial<UserT>;
  };
};

type PropsT = {
  onUpdate: (update: UpdateT) => Promise<void>;
  practitioner?: ArtistT;
  user: UserT;
};

function useSyncState<T>(value: T): [T, (val: T) => void] {
  const [local, setLocal] = useState(value);
  const [prev, setPrev] = useState(value);

  if (value !== prev) {
    setPrev(value);
    setLocal(value);
  }

  return [local, setLocal];
}

const AvatarSection: FunctionComponent<{
  localAvatarUrl?: string;
  onAvatarClick: () => void;
  user: UserT;
}> = ({
  localAvatarUrl,
  onAvatarClick,
  user,
}) => (
  <div styleName="avatarColumn">
    <div styleName="avatarContainer">
      <Avatar
        onClick={onAvatarClick}
        size={120}
        user={
          localAvatarUrl
            ? {
              ...user,
              avatarUrl: localAvatarUrl,
            }
            : user
        }
      />
    </div>
    <div styleName="avatarInfo">
      Click on the avatar to upload a new profile photo (256px square
      recommended)
    </div>
  </div>
);

const PersonalDetailsSection: FunctionComponent<{
  onSectionUpdate: (
    update: UpdateT,
    loadingSetter: (val: boolean) => void,
  ) => Promise<void>;
  personalDetailsLoading: boolean;
  setPersonalDetailsLoading: (val: boolean) => void;
  user: UserT;
}> = ({
  onSectionUpdate,
  personalDetailsLoading,
  setPersonalDetailsLoading,
  user,
}) => {
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [localAvatarFile, setLocalAvatarFile] = useState<File>();
  const [prevAvatarUrl, setPrevAvatarUrl] = useState(user.avatarUrl);
  if (user.avatarUrl !== prevAvatarUrl) {
    setPrevAvatarUrl(user.avatarUrl);
    if (localAvatarFile) setLocalAvatarFile(undefined);
  }

  const [localFirstName, setLocalFirstName] = useSyncState(user.firstName);
  const [localLastName, setLocalLastName] = useSyncState(user.lastName);
  const [localPhone, setLocalPhone] = useSyncState(user.phone);

  const [localPhoneAllowsWhatsApp, setLocalPhoneAllowsWhatsApp] = useSyncState(
    user.phoneAllowsWhatsApp ?? false,
  );
  const [phoneError, setPhoneError] = useState(false);

  const localAvatarUrl = useMemo(() => (
    localAvatarFile ? URL.createObjectURL(localAvatarFile) : undefined
  ), [localAvatarFile]);

  const isPersonalDetailsDirty = !!localAvatarFile
    || localFirstName !== user.firstName
    || localLastName !== user.lastName
    || ((localPhone ?? null) !== (user.phone ?? null))
    || localPhoneAllowsWhatsApp !== (user.phoneAllowsWhatsApp ?? false);

  const isPhoneInvalid = useMemo(() => {
    const isPhoneModified = (localPhone ?? null) !== (user.phone ?? null);
    return isPhoneModified && !!localPhone && !isValidPhone(localPhone);
  }, [localPhone, user.phone]);

  const onSave = useCallback(() => {
    void (async () => {
      const isPhoneModified = (localPhone ?? null) !== (user.phone ?? null);
      if (isPhoneModified && !isValidPhoneOrEmpty(localPhone ?? '')) {
        setPhoneError(true);
        return;
      }
      await onSectionUpdate({
        newAvatar: localAvatarFile,
        practitioner: {
          user: {
            firstName: localFirstName,
            lastName: localLastName,
            phone: localPhone,
            phoneAllowsWhatsApp: localPhoneAllowsWhatsApp,
          },
        },
      }, setPersonalDetailsLoading);
      setPhoneError(false);
      if (localAvatarFile) setLocalAvatarFile(undefined);
    })();
  }, [
    localAvatarFile,
    localFirstName,
    localLastName,
    localPhone,
    localPhoneAllowsWhatsApp,
    onSectionUpdate,
    setPersonalDetailsLoading,
    user.phone,
  ]);

  return (
    <div styleName="section">
      <input
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            setLocalAvatarFile(file);
          }
        }}
        ref={avatarInputRef}
        style={{ display: 'none' }}
        type="file"
      />
      <div styleName="sectionHeader">
        <div styleName="sectionTitle">
          Personal details
        </div>
        <div styleName="tag">never shown publicly</div>
      </div>

      <div styleName="personalDetailsContainer">
        <AvatarSection
          localAvatarUrl={localAvatarUrl}
          onAvatarClick={() => {
            avatarInputRef.current?.click();
          }}
          user={user}
        />

        <div styleName="fieldsColumn">
          <div styleName="fieldRow">
            <Input
              label="First Name"
              onChange={(e) => {
                setLocalFirstName(e.target.value);
              }}
              value={localFirstName}
            />
            <Input
              label="Last Name"
              onChange={(e) => {
                setLocalLastName(e.target.value);
              }}
              value={localLastName}
            />
          </div>

          <Input
            disabled
            label="Email Address"
            type="email"
            value={user.email ?? ''}
          />

          <Input
            error={phoneError}
            label="Mobile Number"
            onBlur={() => {
              setPhoneError(!isValidPhoneOrEmpty(localPhone ?? ''));
            }}
            onChange={(e) => {
              setLocalPhone(e.target.value);
              if (phoneError && isValidPhoneOrEmpty(e.target.value)) {
                setPhoneError(false);
              }
            }}
            type="tel"
            value={localPhone ?? ''}
          />

          <div styleName="leftAlignCheckbox">
            <Checkbox
              checked={localPhoneAllowsWhatsApp}
              label="Same number for WhatsApp"
              onChange={() => {
                setLocalPhoneAllowsWhatsApp(!localPhoneAllowsWhatsApp);
              }}
            />
          </div>
        </div>
      </div>

      <div styleName="footer">
        <Button
          disabled={
            !isPersonalDetailsDirty || isPhoneInvalid
            || personalDetailsLoading
          }
          onClick={onSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const AboutYouSection: FunctionComponent<{
  aboutYouLoading: boolean;
  onSectionUpdate: (
    update: UpdateT,
    loadingSetter: (val: boolean) => void,
  ) => Promise<void>;
  setAboutYouLoading: (val: boolean) => void;
  user: UserT;
}> = ({
  aboutYouLoading,
  onSectionUpdate,
  setAboutYouLoading,
  user,
}) => {
  const [localDateOfBirth, setLocalDateOfBirth] = useSyncState(
    user.dateOfBirth,
  );
  const [localGender, setLocalGender] = useSyncState(user.gender);

  const isAboutYouDirty = (localDateOfBirth ?? null)
    !== (user.dateOfBirth ?? null)
    || (localGender ?? null) !== (user.gender ?? null);

  return (
    <div styleName="section">
      <div styleName="sectionTitle">
        About you
      </div>

      <div styleName="sectionContent">
        <div styleName="fieldRow">
          <Input
            label="Date of Birth"
            max={time().format('YYYY-MM-DD')}
            onChange={(e) => {
              setLocalDateOfBirth(e.target.value);
            }}
            placeholder="mm/dd/yyyy"
            theme={inputThemeFixedSmallLabel}
            type={localDateOfBirth ? 'date' : 'text'}
            value={localDateOfBirth ?? ''}
          />

          <CustomDropdown
            label="Gender"
            onChange={(value) => {
              setLocalGender(value as 'Female' | 'Male');
            }}
            options={['Male', 'Female']}
            value={localGender ?? 'Select'}
          />
        </div>
      </div>

      <div styleName="footer">
        <Button
          disabled={!isAboutYouDirty || aboutYouLoading}
          onClick={() => {
            void onSectionUpdate({
              practitioner: {
                user: {
                  dateOfBirth: localDateOfBirth,
                  gender: localGender,
                },
              },
            }, setAboutYouLoading);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const AddressSection: FunctionComponent<{
  addressLoading: boolean;
  onSectionUpdate: (
    update: UpdateT,
    loadingSetter: (val: boolean) => void,
  ) => Promise<void>;
  setAddressLoading: (val: boolean) => void;
  user: UserT;
}> = ({
  addressLoading,
  onSectionUpdate,
  setAddressLoading,
  user,
}) => {
  const [localAddress, setLocalAddress] = useSyncState(user.address);
  const [localCity, setLocalCity] = useSyncState(user.city);
  const [localState, setLocalState] = useSyncState(user.state);
  const [localZipCode, setLocalZipCode] = useSyncState(user.zipCode);

  const isAddressDirty = (localAddress ?? null) !== (user.address ?? null)
    || (localCity ?? null) !== (user.city ?? null)
    || (localState ?? null) !== (user.state ?? null)
    || (localZipCode ?? null) !== (user.zipCode ?? null);

  return (
    <div styleName="section">
      <div styleName="sectionTitle">
        Address
      </div>

      <div styleName="sectionContent">
        <Input
          label="Address"
          onChange={(e) => {
            setLocalAddress(e.target.value);
          }}
          value={localAddress ?? ''}
        />

        <div styleName="fieldRow">
          <Input
            label="City"
            onChange={(e) => {
              setLocalCity(e.target.value);
            }}
            value={localCity ?? ''}
          />

          <Input
            label="State"
            onChange={(e) => {
              setLocalState(e.target.value);
            }}
            value={localState ?? ''}
          />
        </div>

        <Input
          label="Zip Code"
          onChange={(e) => {
            setLocalZipCode(e.target.value);
          }}
          value={localZipCode ?? ''}
        />
      </div>

      <div styleName="footer">
        <Button
          disabled={!isAddressDirty || addressLoading}
          onClick={() => {
            void onSectionUpdate({
              practitioner: {
                user: {
                  address: localAddress,
                  city: localCity,
                  state: localState,
                  zipCode: localZipCode,
                },
              },
            }, setAddressLoading);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const OnboardingSection: FunctionComponent<{
  onSectionUpdate: (
    update: UpdateT,
    loadingSetter: (val: boolean) => void,
  ) => Promise<void>;
  onboardingLoading: boolean;
  practitioner?: ArtistT;
  setOnboardingLoading: (val: boolean) => void;
  user: UserT;
}> = ({
  onSectionUpdate,
  onboardingLoading,
  practitioner,
  setOnboardingLoading,
  user,
}) => {
  const [localTcConsent, setLocalTcConsent] = useSyncState(
    !!practitioner?.teachingAndContentCreationConsent,
  );

  const [localWantsIntroCall, setLocalWantsIntroCall] = useSyncState(
    !!user.wantToScheduleIntroCall,
  );

  const isOnboardingDirty = localTcConsent
    !== !!practitioner?.teachingAndContentCreationConsent
    || localWantsIntroCall !== !!user.wantToScheduleIntroCall;

  return (
    <div styleName="section">
      <div styleName="sectionTitle">
        Onboarding
      </div>

      <div styleName="sectionContent">
        <div styleName="leftAlignCheckbox">
          <Checkbox
            checked={localTcConsent}
            label="Comfortable creating 2 to 8 minute segments"
            onChange={() => {
              setLocalTcConsent(!localTcConsent);
            }}
          />

          <Checkbox
            checked={localWantsIntroCall}
            label="Wants a 20 minute intro call"
            onChange={() => {
              setLocalWantsIntroCall(!localWantsIntroCall);
            }}
          />
        </div>
      </div>

      <div styleName="footer">
        <Button
          disabled={!isOnboardingDirty || onboardingLoading}
          onClick={() => {
            void onSectionUpdate({
              practitioner: {
                teachingAndContentCreationConsent: localTcConsent,
                user: {
                  wantToScheduleIntroCall: localWantsIntroCall,
                },
              },
            }, setOnboardingLoading);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const Modalities: FunctionComponent<PropsT> = ({
  onUpdate,
  practitioner,
  user,
}) => {
  const onSectionUpdate = useCallback(async (
    update: UpdateT,
    loadingSetter: (val: boolean) => void,
  ) => {
    try {
      loadingSetter(true);
      await onUpdate(update);
    } finally {
      loadingSetter(false);
    }
  }, [onUpdate]);

  const [personalDetailsLoading, setPersonalDetailsLoading] = useState(false);
  const [aboutYouLoading, setAboutYouLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const isSaving = personalDetailsLoading
    || aboutYouLoading
    || addressLoading
    || onboardingLoading;

  const THROBBER_HIDE_DELAY = 1000;
  const [showThrobber, setShowThrobber] = useState(isSaving);
  if (isSaving && !showThrobber) setShowThrobber(true);

  useEffect(() => {
    if (isSaving) return undefined;
    const id = setTimeout(() => {
      setShowThrobber(false);
    }, THROBBER_HIDE_DELAY);
    return () => {
      clearTimeout(id);
    };
  }, [isSaving]);

  return (
    <ThemeProvider
      themes={{ CustomDropdown: dropdownTheme, Input: inputTheme }}
    >
      <div className={S.container}>
        {showThrobber ? (
          <div styleName="savingOverlay">
            <div styleName="savingOverlayInner">
              <Throbber />
            </div>
          </div>
        ) : null}

        <PersonalDetailsSection
          onSectionUpdate={onSectionUpdate}
          personalDetailsLoading={personalDetailsLoading}
          setPersonalDetailsLoading={setPersonalDetailsLoading}
          user={user}
        />
        <AboutYouSection
          aboutYouLoading={aboutYouLoading}
          onSectionUpdate={onSectionUpdate}
          setAboutYouLoading={setAboutYouLoading}
          user={user}
        />
        <AddressSection
          addressLoading={addressLoading}
          onSectionUpdate={onSectionUpdate}
          setAddressLoading={setAddressLoading}
          user={user}
        />
        <OnboardingSection
          onSectionUpdate={onSectionUpdate}
          onboardingLoading={onboardingLoading}
          practitioner={practitioner}
          setOnboardingLoading={setOnboardingLoading}
          user={user}
        />
      </div>
    </ThemeProvider>
  );
};

export default Modalities;
